import _ from 'underscore';
import React from 'react';
import LocalizedMessage from 'common/localized-message';
import UserListIcon from 'hudl-react-kickoff/icons/Userlist';

import * as TeamSortTypes from 'common/team-sort-types';
import TeamControls from 'components/team-profile/team-tab/team-controls';
import AthleteRow from 'components/team-profile/team-tab/athlete-row';
import { Spinner } from 'components/shared/uniform';

import './team-content.scss';

class TeamContent extends React.Component {
  static propTypes = {
    user: React.PropTypes.shape({
      isPrivilegedUser: React.PropTypes.bool,
      lastName: React.PropTypes.string,
    }),
    team: React.PropTypes.shape({
      teamId: React.PropTypes.string.isRequired,
      teamName: React.PropTypes.string,
      schoolName: React.PropTypes.string,
      primaryColor: React.PropTypes.string,
      secondaryColor: React.PropTypes.string,
    }).isRequired,
    seasons: React.PropTypes.arrayOf(React.PropTypes.shape({
      seasonId: React.PropTypes.string.isRequired,
      seasonYear: React.PropTypes.number.isRequired,
      isSelected: React.PropTypes.bool,
      isCurrentSeason: React.PropTypes.bool,
      athleteRequestComplete: React.PropTypes.bool,
      athleteLoadError: React.PropTypes.instanceOf(Error),
      athletes: React.PropTypes.arrayOf(React.PropTypes.shape({
        fullName: React.PropTypes.string,
        highlightViews: React.PropTypes.number,
        lastName: React.PropTypes.string,
        userId: React.PropTypes.string.isRequired,
        isDisabled: React.PropTypes.bool,
        hideFromPublic: React.PropTypes.bool,
      })),
    })).isRequired,
    teamSortType: React.PropTypes.oneOf(Object.keys(TeamSortTypes).map(key => TeamSortTypes[key])).isRequired,
    onSeasonSelect: React.PropTypes.func.isRequired,
    onSortSelect: React.PropTypes.func.isRequired,
    onFollowUnfollow: React.PropTypes.func.isRequired,
    onChangeTab: React.PropTypes.func.isRequired,
    forcePublicView: React.PropTypes.bool,
  }

  getSelectedSeason() {
    return this.props.seasons.find((season) => season.isSelected); // eslint-disable-line react/prop-types
  }

  renderEmptyState() {
    if (this.props.user && this.props.user.isPrivilegedUser && !this.props.forcePublicView) {
      return (
        <div className="tab-blank-state">
          <div>
            <LocalizedMessage id="profiles.team.update_roster" values={{name: this.props.user.lastName}}/>
          </div>
          <UserListIcon className="roster-icon blank-icon"/>
        </div>
      );
    }
    return (
      <div className="team-empty-state">
        <h1>
          {this.props.team.teamName} has not added any players for this season.
        </h1>
        <UserListIcon />
        <div className="empty-state-smaller-text">
          Check back later. Until then why don't you check out the Timeline.
        </div>
        <a href="#" className="uni-btn uni-btn--primary uni-btn--small timeline-button" onClick={(e) => {
          e.preventDefault();
          this.props.onChangeTab('timeline');
          return false;
        }}>
          <span>Check out the Timeline</span>
        </a>
      </div>
    );
  }

  compareAthleteByJersey(athlete1, athlete2) {
    const hasJersey1 = athlete1.jerseyNumber !== null && !isNaN(athlete1.jerseyNumber);
    const hasJersey2 = athlete2.jerseyNumber !== null && !isNaN(athlete2.jerseyNumber);
    if (hasJersey1 && hasJersey2) {
      const jersey1 = parseInt(athlete1.jerseyNumber, 10);
      const jersey2 = parseInt(athlete2.jerseyNumber, 10);
      if (jersey1 === jersey2) {
        return this.compareAthleteByName(athlete1, athlete2);
      }
      return jersey1 > jersey2 ? 1 : -1;
    } else if (!hasJersey1 && !hasJersey2) {
      return this.compareAthleteByName(athlete1, athlete2);
    } else if (hasJersey1) {
      return -1;
    }
    return 1;
  }

  compareAthleteByName(athlete1, athlete2) {
    const lastName1 = (athlete1.lastName || '').toLowerCase();
    const lastName2 = (athlete2.lastName || '').toLowerCase();
    const fullName1 = athlete1.fullName.toLowerCase();
    const fullName2 = athlete2.fullName.toLowerCase();
    const lastNamesAreSame = lastName1 === lastName2;

    if (lastNamesAreSame) {
      return fullName1 > fullName2 ? 1 : -1;
    }

    return lastName1 > lastName2 ? 1 : -1;
  }

  getTeamColors() {
    const team = this.props.team;
    const primaryColor = team.primaryColor;
    const secondaryColor = team.secondaryColor;

    return {
      primaryColor,
      secondaryColor,
    };
  }

  renderAthletes() {
    // Check request state before returning athlete rows
    const season = this.getSelectedSeason();
    if (!season.athleteRequestComplete) {
      return (
        <div className="requesting-season-athletes uni-pad--two--vert">
          <Spinner size="large" />
        </div>
      );
    } else if (season.athleteLoadError) {
      return (
        <div className="load-error-season-athletes">
          <p>There was an error loading these athletes. Please try again later.</p>
        </div>
      );
    } else if (!season.athletes || !season.athletes.length) {
      return this.renderEmptyState();
    }

    const athleteRows = [];
    const publicView = this.props.forcePublicView || !this.props.user || !this.props.user.isPrivilegedUser;
    let athletes;
    if (season.isCurrentSeason) {
      athletes = !publicView ? season.athletes : _.filter(season.athletes, a => !a.isDisabled && !a.hideFromPublic);
    } else {
      athletes = !publicView ? season.athletes : _.filter(season.athletes, a => !a.hideFromPublic);
    }

    if (!athletes.length) {
      return this.renderEmptyState();
    }

    const colors = this.getTeamColors();

    switch (this.props.teamSortType) {
    case TeamSortTypes.ALPHABETICAL:
      const alphaSortedList = athletes.sort(this.compareAthleteByName.bind(this));

      alphaSortedList.forEach((athlete) => {
        athleteRows.push(
          <AthleteRow
            key={athlete.userId}
            athlete={athlete}
            user={this.props.user}
            onFollowUnfollow={this.props.onFollowUnfollow}
            {...colors}
          />
        );
      });
      break;
    case TeamSortTypes.JERSEY_NUMBER:
      const numberSortedList = athletes.sort(this.compareAthleteByJersey.bind(this));

      numberSortedList.forEach(athlete => {
        athleteRows.push(
          <AthleteRow
            key={athlete.userId}
            athlete={athlete}
            user={this.props.user}
            onFollowUnfollow={this.props.onFollowUnfollow}
            {...colors}
          />
        );
      });
      break;
    case TeamSortTypes.MOST_VIEWS:
    default:
      const viewSortedList = _.sortBy(athletes, (athlete) => {
        return -athlete.highlightViews;
      });
      viewSortedList.forEach(athlete => {
        athleteRows.push(
          <AthleteRow
            key={athlete.userId}
            athlete={athlete}
            user={this.props.user}
            forcePublicView={this.props.forcePublicView}
            onFollowUnfollow={this.props.onFollowUnfollow}
            {...colors}
          />
        );
      });

      break;
    }

    return athleteRows;
  }

  renderControls() {
    return (
      <TeamControls
        forcePublicView={this.props.forcePublicView}
        user={this.props.user}
        team={this.props.team}
        seasons={this.props.seasons}
        teamSortType={this.props.teamSortType}
        onSeasonSelect={this.props.onSeasonSelect}
        onSortSelect={this.props.onSortSelect}
      />
    );
  }

  render() {
    return (
      <div className="team-content compact-margin">
        {this.renderControls()}
        {this.renderAthletes()}
      </div>
    );
  }
}

export default TeamContent;
